import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, CheckCircle2, XCircle, Inbox, Plus, PenLine } from "lucide-react";

const STATUS_BADGE = { submitted: "badge badge-pending", processing: "badge badge-info", ready: "badge badge-approved", released: "badge badge-approved", rejected: "badge badge-destructive" };

export default function DocumentRequests() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [nf, setNf] = useState({ employee_id: "", doc_type: "", details: {} });
    const [sigOpen, setSigOpen] = useState(false);
    const [sig, setSig] = useState({ signatory_name: "", signatory_title: "", signature_image: "", has_signature: false });

    const openSig = async () => {
        try { const { data: d } = await api.get("/hris/company-signature"); setSig({ ...d, signature_image: "" }); } catch {}
        setSigOpen(true);
    };
    const onSigFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => setSig((s) => ({ ...s, signature_image: reader.result }));
        reader.readAsDataURL(f);
    };
    const saveSig = async () => {
        try {
            const payload = { signatory_name: sig.signatory_name, signatory_title: sig.signatory_title };
            if (sig.signature_image) payload.signature_image = sig.signature_image;
            await api.put("/hris/company-signature", payload);
            toast.success("E-signature saved — released documents now come out signed");
            setSigOpen(false);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const load = () => api.get(`/document-requests/queue${filter ? `?status=${filter}` : ""}`).then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, [filter]);
    useEffect(() => { api.get("/document-requests/catalog").then((r) => setCatalog(r.data.categories || [])).catch(() => {}); }, []);

    const allTypes = catalog.flatMap((c) => c.types.map((t) => ({ ...t, cat: c.name })));
    const selType = allTypes.find((t) => t.key === nf.doc_type);

    const pickType = (key) => {
        const t = allTypes.find((x) => x.key === key);
        const details = {};
        (t?.fields || []).forEach((f) => { if (f.type === "select") details[f.key] = f.options[0]; });
        setNf({ ...nf, doc_type: key, details });
    };

    const createOnBehalf = async () => {
        if (!nf.employee_id) return toast.error("Pick the employee");
        if (!nf.doc_type) return toast.error("Pick a document type");
        for (const f of selType?.fields || []) {
            if (nf.details[f.key] == null || nf.details[f.key] === "") return toast.error(`${f.label} is required`);
        }
        try {
            await api.post("/document-requests/on-behalf", nf);
            toast.success("Request created — it's now in the queue");
            setCreateOpen(false);
            setNf({ employee_id: "", doc_type: "", details: {} });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const act = async (r, status) => {
        let note = "";
        if (status === "rejected") {
            note = window.prompt("Reason for rejection:", "") || "";
            if (!note.trim()) return;
        }
        try {
            await api.post(`/document-requests/${r.id}/status`, { status, note });
            toast.success(status === "released" ? "Marked released — hand the signed copy to the employee" : `Marked ${status}`);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const openPrint = (r) => {
        // Official-form requests print as a merged PDF: pre-filled data sheet +
        // the official government form. Others print the generated letter.
        window.open(`/api/document-requests/${r.id}/${r.has_official_pdf ? "print-pdf" : "print"}`, "_blank");
        if (r.status === "submitted") act(r, "processing");
    };

    return (
        <div>
            <PageHeader title="Document Requests" subtitle="Employee requests for COE, BIR 2316, SSS and Pag-IBIG forms. Open a request to get the PRE-FILLED form — just print, sign, and mark it released."
                actions={
                    <div className="flex items-center gap-2">
                        <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="">All statuses</option>
                            {["submitted", "processing", "ready", "released", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button className="h-9 px-3 rounded-md border border-border text-sm font-medium flex items-center gap-2" onClick={openSig} data-testid="esign-btn">
                            <PenLine className="size-4" /> E-Signature
                        </button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => setCreateOpen(true)} data-testid="hr-create-request-btn">
                            <Plus className="size-4" /> New Request
                        </button>
                    </div>
                } />

            <Dialog open={sigOpen} onOpenChange={setSigOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Company e-signature</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div className="text-xs text-muted-foreground">Uploaded once, applied automatically to every COE and pre-filled form — HR clicks print and the document is already signed. {sig.has_signature && <b className="text-emerald-600">A signature is on file.</b>}</div>
                        <div><Label>Signatory name</Label><Input value={sig.signatory_name || ""} onChange={(e) => setSig({ ...sig, signatory_name: e.target.value })} placeholder="Ana Reyes" data-testid="sig-name" /></div>
                        <div><Label>Title</Label><Input value={sig.signatory_title || ""} onChange={(e) => setSig({ ...sig, signatory_title: e.target.value })} placeholder="HR Director" data-testid="sig-title" /></div>
                        <div>
                            <Label>Signature image (PNG with transparent background works best)</Label>
                            <input type="file" accept="image/png,image/jpeg" onChange={onSigFile} className="w-full text-sm border border-border rounded-md p-2" data-testid="sig-file" />
                            {sig.signature_image && <img src={sig.signature_image} alt="signature preview" className="h-14 mt-2 border border-border rounded p-1" />}
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveSig} data-testid="sig-save">Save signature</button></DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>New request on behalf of an employee</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div>
                            <Label>Employee</Label>
                            <EmployeePicker value={nf.employee_id} onChange={(v) => setNf({ ...nf, employee_id: v })} testId="obh-employee" />
                        </div>
                        <div>
                            <Label>Document</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={nf.doc_type} onChange={(e) => pickType(e.target.value)} data-testid="obh-doctype">
                                <option value="">Pick a document…</option>
                                {catalog.map((c) => (
                                    <optgroup key={c.name} label={c.name}>
                                        {c.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        {(selType?.fields || []).map((f) => (
                            <div key={f.key}>
                                <Label>{f.label}</Label>
                                {f.type === "select" ? (
                                    <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={nf.details[f.key] || ""} onChange={(e) => setNf({ ...nf, details: { ...nf.details, [f.key]: e.target.value } })}>
                                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : (
                                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={nf.details[f.key] || ""} onChange={(e) => setNf({ ...nf, details: { ...nf.details, [f.key]: e.target.value } })} />
                                )}
                            </div>
                        ))}
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createOnBehalf} data-testid="obh-submit">Create request</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {data && (
                <div className="flex items-center gap-2 mb-3 text-xs">
                    <Inbox className="size-4 text-primary" />
                    <span className="badge badge-pending">{data.pending} pending</span>
                    <span className="text-muted-foreground">Workflow: request → open pre-filled form → print & sign → mark released.</span>
                </div>
            )}

            {data && !data.items.length && <Surface className="p-6"><Empty title="No document requests" /></Surface>}
            {data && data.items.length > 0 && (
                <TableWrap>
                    <thead><tr><th>Employee</th><th>Document</th><th>Details</th><th>Requested</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {data.items.map((r) => (
                            <tr key={r.id} data-row-id={r.id} data-testid={`req-${r.id}`}>
                                <td><b>{r.employee_name}</b> <span className="text-xs text-muted-foreground">{r.employee_no}</span></td>
                                <td className="text-sm">{r.doc_label}</td>
                                <td className="text-xs text-muted-foreground max-w-[220px]">{Object.entries(r.details || {}).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") || "—"}</td>
                                <td className="text-xs">{formatDateTime(r.requested_at)}</td>
                                <td><span className={STATUS_BADGE[r.status] || "badge"}>{r.status}</span></td>
                                <td>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button className="h-7 px-2 rounded-md bg-primary text-white text-xs inline-flex items-center gap-1" onClick={() => openPrint(r)} data-testid={`print-${r.id}`}><Printer className="size-3" /> Pre-filled form</button>
                                        {["submitted", "processing"].includes(r.status) && (
                                            <>
                                                <button className="text-xs text-emerald-600 inline-flex items-center gap-1" onClick={() => act(r, "released")} data-testid={`release-${r.id}`}><CheckCircle2 className="size-3" /> Released</button>
                                                <button className="text-xs text-destructive inline-flex items-center gap-1" onClick={() => act(r, "rejected")}><XCircle className="size-3" /> Reject</button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}
        </div>
    );
}
